import React, { useRef, useState } from 'react';
import { Box, IconButton, Link, Typography } from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';

const resolveRadius = (compact, edges) => {
  const r = compact ? '14px' : '16px';
  if (edges === 'top') {
    return {
      borderTopLeftRadius: r,
      borderTopRightRadius: r,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    };
  }
  if (edges === 'none') {
    return { borderRadius: 0 };
  }
  return { borderRadius: r };
};

const ChatImageAttachment = ({ attachment, compact, edges }) => (
  <Box
    component="a"
    href={attachment.url}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      position: 'relative',
      display: 'block',
      width: '100%',
      lineHeight: 0,
      bgcolor: 'action.hover',
      overflow: 'hidden',
      ...resolveRadius(compact, edges),
    }}
  >
    <Box
      component="img"
      src={attachment.url}
      alt={attachment.fileName || 'Image'}
      sx={{
        display: 'block',
        width: '100%',
        maxHeight: compact ? 260 : 320,
        objectFit: 'cover',
      }}
    />
  </Box>
);

const ChatVideoAttachment = ({ attachment, compact, edges }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const mediaUrl = String(attachment.url || '');
  const previewUrl = mediaUrl.includes('#') ? mediaUrl : `${mediaUrl}#t=0.001`;

  const handlePlay = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    try {
      setPlaying(true);
      video.controls = true;
      await video.play();
    } catch {
      setPlaying(false);
      if (video) video.controls = false;
    }
  };

  const handlePauseOrEnded = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused || video.ended) {
      setPlaying(false);
      video.controls = false;
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'block',
        width: '100%',
        lineHeight: 0,
        bgcolor: 'text.primary',
        overflow: 'hidden',
        ...resolveRadius(compact, edges),
      }}
    >
      <Box
        component="video"
        ref={videoRef}
        src={previewUrl}
        preload="metadata"
        playsInline
        onPause={handlePauseOrEnded}
        onEnded={handlePauseOrEnded}
        sx={{
          display: 'block',
          width: '100%',
          maxHeight: compact ? 260 : 320,
          bgcolor: 'text.primary',
        }}
      >
        <track kind="captions" />
      </Box>

      {!playing ? (
        <IconButton
          aria-label="Play video"
          onClick={handlePlay}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: compact ? 56 : 64,
            height: compact ? 56 : 64,
            bgcolor: 'rgba(255,255,255,0.92)',
            color: 'text.primary',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            '&:hover': { bgcolor: 'background.paper' },
          }}
        >
          <PlayArrowRoundedIcon sx={{ fontSize: compact ? 34 : 40, ml: '2px' }} />
        </IconButton>
      ) : null}
    </Box>
  );
};

/**
 * App-style inline image / video / file attachments for chat messages.
 * `edges`: "all" | "top" — card corner treatment when nested in a media message card.
 */
const CommunityChatAttachments = ({ attachments = [], compact = false, edges = 'all' }) => {
  if (!attachments.length) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        width: '100%',
      }}
    >
      {attachments.map((attachment, index) => {
        const itemEdges = index === 0 ? edges : 'none';

        if (attachment.mediaKind === 'image') {
          return (
            <ChatImageAttachment
              key={attachment.id}
              attachment={attachment}
              compact={compact}
              edges={itemEdges}
            />
          );
        }

        if (attachment.mediaKind === 'video') {
          return (
            <ChatVideoAttachment
              key={attachment.id}
              attachment={attachment}
              compact={compact}
              edges={itemEdges}
            />
          );
        }

        return (
          <Link
            key={attachment.id}
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 1,
              m: compact ? 1.25 : 1.5,
              borderRadius: '10px',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
              color: 'text.secondary',
            }}
          >
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            <Typography
              noWrap
              sx={{ fontSize: compact ? '0.75rem' : '0.8125rem', fontWeight: 600 }}
            >
              {attachment.fileName || 'File'}
            </Typography>
          </Link>
        );
      })}
    </Box>
  );
};

export default CommunityChatAttachments;
